<template>
  <div class="space-y-2">
    <!-- Reactivation Activation -->
    <div class="brand-card-lg" v-if="true">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-blue opacity-30" />
          <font-awesome-icon :icon="['fas', 'plug']" class="text-brand-blue text-xl" />
        </font-awesome-layers>
        <span class="title -mt-2">Reactivation Setup</span>
      </div>
      <div class="mt-0 pl-16">
        <p class="font-semibold text-brand-brown text-md">Start reactivating canceled customers with reactivation campaigns.</p>
      </div>
      <div class="mt-4 pl-16">
        <checklist-item :completed="isCustomDomainVerified">
          <span v-if="!isCustomDomainVerified">
            <router-link to="/settings/custom-domains">
              Add and verify a <b class="text-brand-brown">Checkout Domain</b> to host the reactivation page. Outgoing reactivation emails will link
              to this page.
            </router-link>
          </span>
          <span v-else>
            Your reactivation page is hosted at
            <a
              :href="`https://${primaryCheckoutDomain.url}/demo-reactivation`"
              target="_blank"
              class="font-semibold text-brand-brown cursor-pointer underline"
            >
              {{ primaryCheckoutDomain.url }}
            </a>
          </span>
        </checklist-item>
        <checklist-item :completed="hasCheckoutSettings">
          Configure your <a class="font-semibold text-brand-brown cursor-pointer" @click="showCheckoutSettings = true">Checkout Page Settings</a>.
        </checklist-item>
        <checklist-item :completed="isSenderDomainVerified">
          <span v-if="!isSenderDomainVerified">
            Add and verify an <b class="text-brand-brown">Email Sender Domain</b>. Outgoing reactivation emails will come from addresses on this
            domain.
          </span>
          <span v-else>
            Your reactivation campaigns can deliver emails from any address on the following domains:
            <ul>
              <li v-for="domain in verifiedSenderDomains" :key="domain.id">
                <b class="text-brand-brown">@{{ domain.url }}</b>
              </li>
            </ul>
          </span>
        </checklist-item>
      </div>
    </div>

    <!-- BCC Addresses -->
    <div class="brand-card-lg">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-purple-200" />
          <font-awesome-icon :icon="['fas', 'share']" class="text-purple-600 opacity-50 text-xl" />
        </font-awesome-layers>
        <span class="title -mt-2">BCC Reactivation Emails</span>
      </div>
      <div class="mt-0 pb-2 pl-16">
        <p class="font-semibold text-brand-brown text-md">Send reactivation emails to internal addresses or your CRM.</p>
      </div>
      <div class="pl-16" v-loading="loading">
        <ReactivationBcc />
      </div>
    </div>

    <!-- Upload Unsubscribers -->
    <div class="brand-card-lg">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-fuschia-200" />
          <font-awesome-icon :icon="['fas', 'envelope-open']" class="text-fuschia-600 opacity-50 text-xl" />
        </font-awesome-layers>
        <span class="title -mt-2">Unsubscribe List</span>
      </div>
      <div class="mt-0 pb-2 pl-16">
        <p class="font-semibold text-brand-brown text-md">Upload a list of emails to exclude them from reactivation campaigns.</p>
      </div>
      <div class="pl-16" v-loading="loading">
        <UnsubscribeUploader />
      </div>
    </div>
  </div>
</template>

<script>
import { SYNC_USER_RECORD } from '@/store/action-types';
import ChecklistItem from '@/components/UI/ChecklistItem.vue';
import UnsubscribeUploader from '@/components/UI/UnsubscribeUploader.vue';
import SenderDomainsConfig from '@/components/Settings/CustomDomains/SenderDomainsConfig.vue';
import CheckoutDomainsConfig from '@/components/Settings/CustomDomains/CheckoutDomainsConfig.vue';
import ReactivationBcc from '@/components/Reactivation/Settings/ReactivationBcc.vue';

export default {
  name: 'ReactivationSettings',
  components: {
    ChecklistItem,
    UnsubscribeUploader,
    SenderDomainsConfig,
    CheckoutDomainsConfig,
    ReactivationBcc,
  },
  data() {
    return {
      loading: true,
      isActivatingReactivation: false,
      checkoutPage: {
        ckSubdomain: null,
        customDomain: {
          id: null,
          url: null,
          dnsTarget: null,
          isResolving: false,
          hasSSL: false,
          status: null,
          lastChecked: null,
        },
        redirectLink: null,
      },
    };
  },

  computed: {
    dunning() {
      return this.$store.state.org?.dunning || {};
    },
    reactivation() {
      return this.$store.state.org?.reactivation || {};
    },
    senderDomains() {
      return this.dunning?.senderDomains || [];
    },
    isSenderDomainVerified() {
      return this.senderDomains.some((domain) => domain?.returnPathVerified && domain?.dkimVerified);
    },
    verifiedSenderDomains() {
      return this.senderDomains.filter((domain) => domain?.returnPathVerified && domain?.dkimVerified);
    },
    redirectLink() {
      return this.dunning?.checkoutPage.redirectLink;
    },
    hasCheckoutSettings() {
      const { redirectLink, ckSubdomain } = this.dunning?.checkoutPage;
      if (!redirectLink || !ckSubdomain) return false;
      return true;
    },
    subdomain() {
      return this.dunning?.checkoutPage?.ckSubdomain;
    },
    hasSubdomain() {
      return !!this.subdomain;
    },
    primaryCheckoutDomain() {
      return this.dunning?.checkoutPage?.customDomain;
    },
    customDomains() {
      return this.dunning?.checkoutPage?.customDomains || [];
    },
    hasCustomDomain() {
      return this.customDomains.some((domain) => !!domain?.url);
    },
    isCustomDomainVerified() {
      return this.customDomains.some((domain) => domain?.isResolving && domain?.hasSSL);
    },
    verifiedCustomDomains() {
      return this.customDomains.filter((domain) => domain?.isResolving && domain?.hasSSL);
    },
    isReactivationActivated() {
      return this.reactivation?.enabled;
    },
    isReadyToActivate() {
      if (this.isSenderDomainVerified) {
        // Now we require both custom domain AND subdomain
        return this.isCustomDomainVerified && this.hasSubdomain;
      }
      return false;
    },
  },
  async created() {
    if (!this.$store.state.org) {
      await this.$store.dispatch(SYNC_USER_RECORD);
    }
    this.org = this.$store.state.org;
    if (this.hasSubdomain) {
      this.checkoutPage.ckSubdomain = this.subdomain;
    }
    this.loading = false;
  },
  methods: {
    async reloadData() {
      await this.$store.dispatch(SYNC_USER_RECORD);
    },
    displayError(error) {
      if (error?.response?.data?.message) {
        this.$notify({ title: `Error: ${error.response.data.message}`, type: 'error' });
      } else {
        this.$notify({ title: 'Error. Try again or contact us via chat for help.', type: 'error' });
      }
    },
  },
};
</script>
